import React from 'react';

// Components
import Navigation from './components/Navigation';
import HeroSection from './components/HeroSection';
import AboutSection from './components/AboutSection';
import MenuSection from './components/MenuSection';
import GallerySection from './components/GallerySection';
import ContactSection from './components/ContactSection';
import ImageModal from './components/ImageModal';
import Footer from './components/Footer';

// Custom Hooks
import { useImageModal } from './hooks/useImageModal';
import { useMenuModal } from './hooks/useMenuModal';

const NouValentino = () => {
  
  // Image modal functionality
  const {
    showImageModal,
    currentImageIndex,
    setCurrentImageIndex,
    openImageModal,
    closeImageModal
  } = useImageModal();

  // Menu modal functionality
  const {
    showMenuModal,
    currentMenuItem,
    currentMenuItems,
    currentMenuIndex,
    openMenuModal,
    closeMenuModal,
    setMenuIndex
  } = useMenuModal();

  const scrollToSection = (section) => {
    document.getElementById(section)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slide-in-left {
          from {
            opacity: 0;
            transform: translateX(-50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes slide-in-right {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 1s ease-out forwards;
          opacity: 0;
        }
        
        .animate-slide-in-left {
          animation: slide-in-left 1s ease-out forwards;
          opacity: 0;
        }
        
        .animate-slide-in-right {
          animation: slide-in-right 1s ease-out forwards;
          opacity: 0;
        }
        
        .animate-scale-in {
          animation: scale-in 1s ease-out forwards;
          opacity: 0;
        }
        
        .luxury-shadow {
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }
      `}</style>
      
      {/* Global Fixed Logo Background - Visible through all sections */}
      <div className="fixed inset-0 flex items-center justify-center gap-4 pointer-events-none z-[5]">
        {/* Cow logo - Left side - Double size - Positioned close to left edge */}
        <img 
          src="/images/cow_logo.jpg" 
          alt="Cow logo background" 
          className="ml-2 md:ml-4 lg:ml-6 h-64 md:h-96 lg:h-[32rem] xl:h-[40rem] w-auto object-contain opacity-50 hover:opacity-70 transition-opacity duration-500"
          onError={(e) => {e.target.style.display = 'none'}}
        />
        
        {/* Lobster logo - Right side - Double size - Flipped to face cow - Positioned close to right edge */}
        <img 
          src="/images/lobster_logo_light.jpg" 
          alt="Lobster logo background" 
          className="mr-2 md:mr-4 lg:mr-6 h-64 md:h-96 lg:h-[32rem] xl:h-[40rem] w-auto object-contain opacity-50 hover:opacity-70 transition-opacity duration-500 scale-x-[-1]"
          onError={(e) => {e.target.style.display = 'none'}}
        />
      </div>
      
      <div className="min-h-screen overflow-x-hidden">
        {/* Navigation - Higher z-index to stay above logos */}
        <div className={`relative z-50 transition-opacity duration-300 ${showMenuModal ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
          <Navigation scrollToSection={scrollToSection} />
        </div>

        {/* All content sections with z-index above logos */}
        <div className="relative z-10">
          {/* Hero Section */}
          <HeroSection scrollToSection={scrollToSection} />

          {/* About Us Section */}
          <AboutSection />

          {/* Menu Section */}
          <MenuSection
            showMenuModal={showMenuModal}
            currentMenuItem={currentMenuItem}
            currentMenuItems={currentMenuItems}
            currentMenuIndex={currentMenuIndex}
            openMenuModal={openMenuModal}
            closeMenuModal={closeMenuModal}
            setMenuIndex={setMenuIndex}
          />

          {/* Gallery Section */}
          <GallerySection openImageModal={openImageModal} />

          {/* Contact & Location */}
          <ContactSection />

          {/* Footer */}
          <Footer />
        </div>

        {/* Image Modal - Highest z-index */}
        <ImageModal
          showImageModal={showImageModal}
          currentImageIndex={currentImageIndex}
          setCurrentImageIndex={setCurrentImageIndex}
          closeImageModal={closeImageModal}
        />
      </div>
    </>
  );
};

export default NouValentino;